'use client'

import * as React from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'

import { Input } from './input'
import { Label } from './label'
import { PortaledPopoverContent } from './portaled-popover-content'
import { Spinner } from './spinner'
import { useScrollableInPortal } from '../../hooks/use-scrollable-in-portal'
import { cn } from '../../utils'

/** A normalised place suggestion as returned by the backend places proxy. */
export interface PlaceSuggestion {
  id: string
  mainText: string
  secondaryText?: string | null
  description: string
}

/** Resolved place details (matches backend `PlaceDetails`). */
export interface PlaceDetailsResolved {
  placeId: string
  formattedAddress: string | null
  city: string | null
  state: string | null
  zip: string | null
  countryCode: string | null
  countryId: string | null
  country: { id: string; code: string; name: string } | null
  latitude: number | null
  longitude: number | null
}

interface PlacesAddressAutocompleteBaseProps {
  /** Async loader for autocomplete suggestions. */
  fetchSuggestions: (input: string) => Promise<PlaceSuggestion[]>
  /** Async loader for details given a place id. */
  fetchDetails: (placeId: string) => Promise<PlaceDetailsResolved>

  label?: string
  labelClassName?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: string

  /** Min characters before firing autocomplete. Defaults to 2. */
  minLength?: number
  /** Debounce delay in ms. Defaults to 250. */
  debounceMs?: number
  className?: string
}

/**
 * Place mode (default): the field owns a fully-resolved place. Picking a
 * suggestion fetches details and emits the whole `PlaceDetailsResolved`.
 */
interface PlacesAddressAutocompletePlaceProps
  extends PlacesAddressAutocompleteBaseProps {
  mode?: 'place'
  /** Currently-selected resolved place (for displaying the chosen value). */
  value?: Pick<PlaceDetailsResolved, 'city' | 'country' | 'formattedAddress'> | null
  /** Fired when the user picks a suggestion and details have resolved. */
  onChange: (place: PlaceDetailsResolved | null) => void
  /**
   * Optional side-channel of the resolved details, fired alongside `onChange`.
   * Lets a domain wrapper map `PlaceDetailsResolved` to its own shape
   * (e.g. tenant-web's `AddressInput`) without this package importing it.
   */
  onResolved?: (place: PlaceDetailsResolved) => void
}

/**
 * String mode: the field owns a plain display string (city / address text).
 * Picking a suggestion emits its main text; no details round-trip is needed
 * for the value, though one still fires `onResolved` for callers that want it.
 */
interface PlacesAddressAutocompleteStringProps
  extends PlacesAddressAutocompleteBaseProps {
  mode: 'string'
  value?: string | null
  onChange: (value: string) => void
  onResolved?: (place: PlaceDetailsResolved) => void
}

export type PlacesAddressAutocompleteProps =
  | PlacesAddressAutocompletePlaceProps
  | PlacesAddressAutocompleteStringProps

function useDebounced<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = React.useState(value)
  React.useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(handle)
  }, [value, delayMs])
  return debounced
}

export const PlacesAddressAutocomplete: React.FC<
  PlacesAddressAutocompleteProps
> = (props) => {
  const {
    fetchSuggestions,
    fetchDetails,
    label,
    labelClassName,
    placeholder = 'Start typing an address…',
    required,
    disabled,
    error,
    minLength = 2,
    debounceMs = 250,
    className,
  } = props

  const initialDisplay = React.useMemo(() => {
    const value = props.value
    if (!value) return ''
    if (typeof value === 'string') return value
    if (value.formattedAddress) return value.formattedAddress
    return [value.city, value.country?.name].filter(Boolean).join(', ')
  }, [props.value])

  const [inputText, setInputText] = React.useState(initialDisplay)
  const [open, setOpen] = React.useState(false)
  const [suggestions, setSuggestions] = React.useState<PlaceSuggestion[]>([])
  const [isFetching, setIsFetching] = React.useState(false)
  const [isResolving, setIsResolving] = React.useState(false)

  React.useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  // Keep the visible text in sync if the parent changes `value`.
  const lastInitialRef = React.useRef(initialDisplay)
  React.useEffect(() => {
    if (initialDisplay !== lastInitialRef.current) {
      setInputText(initialDisplay)
      lastInitialRef.current = initialDisplay
    }
  }, [initialDisplay])

  const debouncedQuery = useDebounced(inputText.trim(), debounceMs)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const portalScroll = useScrollableInPortal()

  // Fetch suggestions on debounced query change.
  React.useEffect(() => {
    let cancelled = false
    if (!open || disabled) return
    if (!debouncedQuery || debouncedQuery.length < minLength) {
      setSuggestions([])
      return
    }
    setIsFetching(true)
    fetchSuggestions(debouncedQuery)
      .then((list) => {
        if (!cancelled) setSuggestions(list)
      })
      .catch(() => {
        if (!cancelled) setSuggestions([])
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false)
      })
    return () => {
      cancelled = true
    }
  }, [debouncedQuery, minLength, open, disabled, fetchSuggestions])

  const handleSelect = React.useCallback(
    async (s: PlaceSuggestion) => {
      // String mode: the value IS the picked text — emit immediately so the
      // field works even if the details round-trip is slow or fails. Still
      // resolve details for `onResolved` consumers (best-effort).
      if (props.mode === 'string') {
        const { onChange, onResolved } = props
        setInputText(s.mainText)
        setOpen(false)
        onChange(s.mainText)
        if (onResolved) {
          try {
            onResolved(await fetchDetails(s.id))
          } catch {
            /* best-effort side-channel */
          }
        }
        return
      }
      // Place mode: the value IS the resolved details.
      const { onChange, onResolved } = props
      try {
        setIsResolving(true)
        setInputText(s.description)
        setOpen(false)
        const details = await fetchDetails(s.id)
        onChange(details)
        onResolved?.(details)
      } catch {
        // Leave the typed text in place; parent decides UX from `error`.
      } finally {
        setIsResolving(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchDetails, props.mode, props.onChange, props.onResolved],
  )

  const showDropdown =
    !disabled && open && suggestions.length > 0 && !isResolving

  return (
    <div className={cn('relative w-full space-y-2', className)}>
      {label && (
        <Label className={labelClassName}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      <PopoverPrimitive.Root open={showDropdown} onOpenChange={setOpen}>
        <PopoverPrimitive.Anchor asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              type="text"
              placeholder={placeholder}
              value={inputText}
              disabled={disabled || isResolving}
              onChange={(e) => {
                const next = e.target.value
                setInputText(next)
                setOpen(true)
                // Reflect typing into the value: clear when emptied; in string
                // mode the raw text IS the value (so free-typed filters work).
                if (props.mode === 'string') {
                  props.onChange(next)
                } else if (next === '') {
                  props.onChange(null)
                }
              }}
              onFocus={() => setOpen(true)}
              className={cn(error ? 'border-destructive' : '')}
              aria-invalid={!!error}
              aria-required={required}
            />
            {(isFetching || isResolving) && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Spinner size="sm" />
              </div>
            )}
          </div>
        </PopoverPrimitive.Anchor>

        <PortaledPopoverContent
          maxHeight={240}
          className="w-full min-w-[var(--radix-popper-anchor-width)]"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onFocusOutside={(e) => {
            if (
              inputRef.current?.contains(e.target as Node) ||
              inputRef.current === e.target
            ) {
              e.preventDefault()
            }
          }}
          onInteractOutside={(e) => {
            if (
              inputRef.current?.contains(e.target as Node) ||
              inputRef.current === e.target
            ) {
              e.preventDefault()
            }
          }}
        >
          <div
            ref={portalScroll}
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
          >
            {suggestions.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => handleSelect(s)}
                className="w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors border-b border-border last:border-b-0 flex flex-col gap-1"
              >
                <p className="text-sm font-medium text-foreground">
                  {s.mainText}
                </p>
                {s.secondaryText && (
                  <p className="text-xs text-muted-foreground">
                    {s.secondaryText}
                  </p>
                )}
              </button>
            ))}
          </div>
        </PortaledPopoverContent>
      </PopoverPrimitive.Root>

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
